import qs from 'qs'
import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { Invoice } from '@features/invoicesLegacy/types/invoice'
import { fetchExternalInvoicesForMatching } from '@features/reconciliation/query-api'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { QueryKeys } from '../../../enums/queryKeys'
import { mapBillyExternalInvoiceToInvoice } from '../utils/mapBillyExternalInvoiceToInvoice'

interface UseFetchExternalInvoicesProps {
  contactId?: string
  currencyId?: string
  enabled?: boolean
  isPaid?: boolean
  isVoided?: boolean
}

interface UseFetchExternalInvoicesResponse {
  invoices: Invoice[]
  isLoading: boolean
}

export const useFetchExternalInvoices = ({
  contactId,
  currencyId,
  isPaid,
  isVoided,
  enabled = true,
}: UseFetchExternalInvoicesProps): UseFetchExternalInvoicesResponse => {
  const { organization } = useUserOrganization()

  const queryString = useMemo(
    () =>
      qs.stringify({
        organizationId: organization?.id,
        ...(isPaid !== undefined ? { isPaid } : {}),
        ...(isVoided !== undefined ? { isVoided } : {}),
        ...(contactId ? { contactId } : {}),
        ...(currencyId ? { currencyId } : {}),
      }),
    [contactId, currencyId, isPaid, isVoided, organization?.id],
  )

  const { data, isIdle, isLoading } = useQuery(
    [QueryKeys.ExternalInvoices, queryString],
    () => fetchExternalInvoicesForMatching(queryString),
    {
      enabled: enabled && !!organization?.id,
    },
  )

  const invoices = useMemo(
    () => data?.externalInvoices?.map(mapBillyExternalInvoiceToInvoice) ?? [],
    [data?.externalInvoices],
  )

  return {
    invoices,
    isLoading: enabled && (isIdle || isLoading),
  }
}
